import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useRecordMetadataSchemaDetail } from "~/features/admin/recordMetadataSchema/hooks";
import { toRecordMetadataSchemaDetailUI } from "~/features/admin/recordMetadataSchema/utils/transformers";
import { RecordMetadataSchemaDetailPresenter } from "./RecordMetadataSchemaDetailPresenter";
import { RecordMetadataSchemaFormDialog } from "./RecordMetadataSchemaFormDialog";

interface RecordMetadataSchemaDetailProps {
  id: string;
  /** Back action: close the sheet (peek) or navigate to the list (full page). */
  onBack: () => void;
}

/**
 * Reusable from both the full-page route and the Peek side panel. Owns data
 * and the edit dialog; renders the presenter.
 */
export function RecordMetadataSchemaDetail({ id, onBack }: RecordMetadataSchemaDetailProps) {
  const { t } = useTranslation();
  const { data } = useRecordMetadataSchemaDetail(id);
  const [editOpen, setEditOpen] = useState(false);

  const raw = data.propertySchemaSetsByPk;
  const detail = useMemo(() => (raw ? toRecordMetadataSchemaDetailUI(raw) : null), [raw]);

  if (!detail || !raw) {
    return (
      <div className="rounded-[var(--radius-md)] border border-border-default bg-white px-5 py-12 text-center text-14-regular text-fg-2">
        {t("ui.notFound")}
      </div>
    );
  }

  return (
    <>
      <RecordMetadataSchemaDetailPresenter detail={detail} onBack={onBack} onEdit={() => setEditOpen(true)} />
      <RecordMetadataSchemaFormDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        id={detail.id}
        prefilledAssociations={raw.propertySchemaAssociations}
      />
    </>
  );
}
